import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { IconAlertCircle, IconLoader } from '@tabler/icons-react'
import { getAccount, connect } from '@wagmi/core'
import { closeSnackbar, enqueueSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useAccount } from 'wagmi'
import { useUser } from '@/contexts/UserContext'
import { useWeb3Auth } from './web3auth'
import { web3AuthConnector } from '@/App'
import RootButton from '../Buttons/RootButton'
import { blankAddress } from '../../hooks/useWagmiProvider'
import { useIdSubnetProvider } from '../../hooks/useViemProvider'
import useLocalStorage from '../../hooks/useLocalStorage'
import { UserIdentity, hashList } from '../../utils/utils'
import Loading from '../Loading'
import avatar_01 from '../../assets/avatar/01.png'
import avatar_02 from '../../assets/avatar/02.png'
import avatar_03 from '../../assets/avatar/03.png'
import avatar_04 from '../../assets/avatar/04.png'
import Snackbar from '../Snackbar'

interface ConnectionLoadComponentProps {
  address: string
  flag: React.MutableRefObject<boolean>
  setNextPage: React.Dispatch<React.SetStateAction<string>>
  userInfoRef: any
  version: string
  avatarOptions: any
}

const ConnectionLoadComponent: React.FC<ConnectionLoadComponentProps> = ({
  address,
  setNextPage,
  flag,
  userInfoRef,
  version,
  avatarOptions,
}) => {
  const { t } = useTranslation()
  const { getL1nsName, getL1nsNickname } = useIdSubnetProvider()
  const { setConnection } = useUser()

  const [userIdentity, setUserIdentity] = useLocalStorage<UserIdentity>(`userIdentity-${address}`)
  const userInfo = userInfoRef

  // Fetch User data from chain
  const fetchUserOnChainData = async () => {
    // Use local profile image, fallback to Web3Auth image or default avatar for external wallets.
    const profileImage =
      userIdentity?.profileImage && userIdentity?.profileImage !== avatar_01
        ? userIdentity.profileImage
        : userInfo?.profileImage
          ? userInfo?.profileImage
          : avatar_01

    // Check if user signed in with Social Login or Metamask to define username
    const locallyStoredAdapter = localStorage.getItem('Web3Auth-cachedAdapter')
    const nickname =
      userIdentity?.nickname && userIdentity?.nickname !== locallyStoredAdapter
        ? userIdentity.nickname
        : userInfo?.name
          ? userInfo.name
          : locallyStoredAdapter || 'no display name'

    // Set default username if no identity on chain
    const username = userIdentity?.username ? userIdentity.username : ''

    // Check if user has a username
    try {
      const l1nsUsername = await getL1nsName(address)
      if (!l1nsUsername || l1nsUsername === blankAddress) {
        // if user does not have a username
        setUserIdentity({
          version,
          address,
          username: '',
          nickname,
          profileImage,
          avatarOptions,
        })
        setNextPage('/freeusername')
        setConnection('connected')
        flag.current = false
        return
      }

      // Check if user has a nickname
      try {
        const l1nsNickname = await getL1nsNickname(l1nsUsername || username)
        setUserIdentity({
          version,
          address,
          username: l1nsUsername || '',
          nickname: l1nsNickname || nickname,
          profileImage,
          avatarOptions,
        })
      } catch (error: any) {
        // if not connected to ID subnet chain or error when connecting
        setUserIdentity({
          version,
          address,
          username: l1nsUsername || '',
          nickname,
          profileImage,
          avatarOptions,
        })
        closeSnackbar()
        enqueueSnackbar(
          <Snackbar
            value={t(
              'errors.snackbar.idSubnet.displayName',
              `Error fetching Display Name for {{username}}}. Using locally stored name if any.`,
              {
                username: l1nsUsername || username,
              }
            )}
            title={t('errors.snackbar.idSubnet.title', 'Not connected to ID Subnet')}
            icon={<IconAlertCircle size="14px" />}
          />
        )
        console.log('error', error)
      }
    } catch (error: any) {
      // if not connected to ID subnet chain or error when connecting
      setUserIdentity({
        version,
        address,
        username,
        nickname,
        profileImage,
        avatarOptions,
      })
      closeSnackbar()
      enqueueSnackbar(
        <Snackbar
          value={t(
            'errors.snackbar.idSubnet.username',
            `Error fetching Username for {{address}}. Using locally stored username if any.`,
            {
              address,
            }
          )}
          title={t('errors.snackbar.idSubnet.title', 'Not connected to ID Subnet')}
          icon={<IconAlertCircle size="14px" />}
        />
      )
      console.log('error', error)
    }

    // Unless user has no username, set next page to home and set connection to connected
    setNextPage('/home')
    setConnection('connected')
    flag.current = false
  }

  useEffect(() => {
    fetchUserOnChainData()
  }, [])

  return <Loading />
}

interface Web3ModalProps {
  setNextPage: React.Dispatch<React.SetStateAction<string>>
  loggedIn: boolean
}
const Web3Modal: React.FC<Web3ModalProps> = ({ setNextPage, loggedIn = false }) => {
  const { t } = useTranslation()
  const { isLoading, login, getUserInfo } = useWeb3Auth()
  const { isConnected } = getAccount()
  const { connection, setConnection } = useUser()
  const { address } = useAccount()
  const [loadingRedirect, setLoadingRedirect] = useState<boolean>(false)
  const loadingConnection = useRef<boolean>(false)
  const intervalId = useRef<any>(null)
  const addressRef = useRef<string>('')
  const userInfoRef = useRef<any>({})

  /* -------------------------------------------------------------------------- */
  /*       Redefine local userIdentity variable to persist across versions      */
  /* -------------------------------------------------------------------------- */
  const version = useMemo(
    () => hashList([avatar_01, avatar_02, avatar_03, avatar_04]),
    [avatar_01, avatar_02, avatar_03, avatar_04]
  )

  // Default avatar options
  const avatarDefaultOptions = useMemo(
    () => [
      { avatars: avatar_01, fileSize: 0 },
      { avatars: avatar_02, fileSize: 0 },
      { avatars: avatar_03, fileSize: 0 },
      { avatars: avatar_04, fileSize: 0 },
    ],
    [avatar_01, avatar_02, avatar_03, avatar_04]
  )

  // Get locally stored avatarOptions to persist across versions
  const avatarOptions = useMemo(() => {
    const identity = localStorage.getItem(`userIdentity-${address}`)
    const avatars = identity ? JSON.parse(identity).avatarOptions : avatarDefaultOptions
    const newAvatarOptions =
      avatars.length > avatarDefaultOptions.length
        ? avatars.slice(0, avatars.length - avatarDefaultOptions.length)
        : []
    const retval = [...newAvatarOptions, ...avatarDefaultOptions]
    return retval
  }, [address])

  // Get locally stored profile image to persist across versions
  const savedProfileImage = useMemo(() => {
    const identity = localStorage.getItem(`userIdentity-${address}`)
    const profileImage: string = identity ? JSON.parse(identity).profileImage : avatar_01
    // previous profileImage is not in the avatarOptions list, use default
    if (!avatarOptions.some((avatar: any) => avatar.avatars === profileImage)) {
      // if profileImage does not include an underscore it must not be an avatar neither an uploaded image that was carried over from a previous version, use default avatar
      if (!profileImage.includes('_')) {
        return avatar_01
      }
      // match string name of profileImage with avatarDefaultOptions index, p.e. if profileImage is "*/01_*", use avatar_01 from avatarDefaultOptions, if '*/02_*', use avatar_02, etc.

      // get the split from the last '/'
      const firstSplit = profileImage.split('/').slice(-1)
      if (firstSplit.length > 0) {
        const profileImageIndex = Number(firstSplit[0].split('_')[0]) || 1
        console.log('profileImageIndex 1', profileImageIndex)
        if (profileImageIndex > avatarDefaultOptions.length) {
          return avatar_01
        }
        return avatarDefaultOptions[profileImageIndex - 1].avatars
      }
      // if no '/' is found, split with '_' and use first element as index
      const secondSplit = profileImage.split('_')
      const profileImageIndex = Number(secondSplit[0]) || 1
      console.log('profileImageIndex 2', profileImageIndex)
      if (profileImageIndex > avatarDefaultOptions.length) {
        return avatar_01
      }
      return avatarDefaultOptions[profileImageIndex - 1].avatars
    }
    return profileImage
  }, [address, avatarOptions])

  // Delete locally stored `userIdentity-${address}` if version key does include avatarHash variable
  const deleteUserIdentity = useCallback(() => {
    console.log('deleteUserIdentity', address, version, avatarOptions, savedProfileImage)
    const identity = localStorage.getItem(`userIdentity-${address}`)
    const sameVersion = Object.entries(identity || {}).some(
      elem =>
        // compare version to version
        (elem[0] === 'version' && elem[1] === version) ||
        // create hash from avatarOptions avatars and compare to version
        (elem[0] === 'avatarOptions' &&
          hashList(
            Object.entries(elem[1])
              .slice(-avatarDefaultOptions.length)
              .map((avatar: any) => avatar.avatars)
          ) === version)
    )
    // If version is different, delete locally stored `userIdentity-${address}` and create new one
    if (!sameVersion) {
      localStorage.removeItem(`userIdentity-${address}`)
    }
    // If version is the same, update locally stored `userIdentity-${address}` with new avatarOptions
    if (!identity || !sameVersion) {
      localStorage.setItem(
        `userIdentity-${address}`,
        JSON.stringify({
          version,
          address,
          username: '',
          nickname: '',
          profileImage: savedProfileImage,
          avatarOptions,
        })
      )
      console.log('inside', !sameVersion)
      return
    }
    // Regardless of versoin, update locally stored `userIdentity-${address}` with new avatarOptions and savedProfileImage
    console.log('sameVersion', sameVersion)
    console.log('avatarOptions', avatarOptions)
    console.log('savedProfileImage', savedProfileImage)
    localStorage.setItem(
      `userIdentity-${address}`,
      JSON.stringify({
        ...JSON.parse(identity),
        profileImage: savedProfileImage,
        avatarOptions,
      })
    )
  }, [address, version])

  useEffect(() => {
    deleteUserIdentity()
  }, [address, version])
  /* -------------------------------------------------------------------------- */
  /*                                      -                                     */
  /* -------------------------------------------------------------------------- */

  const checkWeb3AuthAndProceed = async () => {
    // Check if user is already connected
    if (localStorage.getItem('Web3Auth-cachedAdapter')) {
      // Retrieve userInfo and address post-login to create userIdentity local variable.
      userInfoRef.current = await getUserInfo()
      const { address: addr } = getAccount()
      addressRef.current = addr as string
      loadingConnection.current = true

      // Set loadingRedirect to false once the necessary actions are taken
      setLoadingRedirect(false)

      // Clear the interval once the necessary actions are taken
      clearInterval(intervalId.current)
    }
  }

  useEffect(() => {
    setLoadingRedirect(true)
    // If cookie is true, wait until Web3Auth is connected
    if (loggedIn) {
      intervalId.current = setInterval(checkWeb3AuthAndProceed, 500)
    } else {
      // If cookie is false, set loadingRedirect to false
      setLoadingRedirect(false)
    }

    // Clear interval after 10 seconds
    setTimeout(() => {
      clearInterval(intervalId.current)
      setLoadingRedirect(false)
    }, 10000)

    // Clear interval on unmount
    return () => {
      clearInterval(intervalId.current)
    }
  }, [])

  // Connect to Web3Auth
  const web3AuthLogin = useCallback(async () => {
    // Check if user is already connected
    if (connection === 'disconnected' && isConnected === false) {
      try {
        await connect({
          connector: web3AuthConnector,
        })
      } catch (error: any) {
        if (error.message.includes('ConnectorAlreadyConnectedError')) {
          setConnection('connected')
          return
        }
      }
    }

    // Login to Web3Auth
    userInfoRef.current = await login()

    // Retrieve address post-login to create userIdentity local variable.
    const { address: addr } = getAccount()
    addressRef.current = addr as string

    // Place after login: manual web3auth modal closure isn't detectable and could leave the button disabled.
    setConnection('connecting')
    loadingConnection.current = true
    setLoadingRedirect(true)
  }, [connection, isConnected, login])

  return (
    <>
      {loadingConnection.current && (
        <ConnectionLoadComponent
          address={addressRef.current}
          setNextPage={setNextPage}
          flag={loadingConnection}
          userInfoRef={userInfoRef.current}
          version={version}
          avatarOptions={avatarOptions}
        />
      )}
      {!loadingConnection.current &&
        (connection === 'connecting' ? (
          <RootButton sx={{ margin: 'auto' }} disabled leftIcon={<IconLoader />}>
            {t('buttons.connecting', 'Connecting...')}
          </RootButton>
        ) : isLoading || loadingRedirect ? (
          <RootButton sx={{ margin: 'auto' }} disabled leftIcon={<IconLoader />}>
            {t('buttons.loadingWeb3Auth', 'Loading Web3Auth...')}
          </RootButton>
        ) : (
          <RootButton sx={{ margin: 'auto' }} transparent onClick={web3AuthLogin}>
            {t('buttons.connectToHub', 'Connect to Hub')}
          </RootButton>
        ))}
    </>
  )
}

export default Web3Modal
